import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, ChevronDown, Menu, X } from "lucide-react";
import { NAV, NAV_SECTIONS } from "@/data/content";
import { useQuiz } from "@/context/QuizContext";
import { useActiveSection } from "@/hooks/useActiveSection";
import { prefetchRoute } from "@/lib/routePrefetch";
import "./Header.css";

const slug = (l) => l.toLowerCase().replace(/\s+/g, "-");

export const Brand = ({ light = true, variant = "header" }) => {
  if (variant === "footer") {
    return (
      <Link to="/" data-testid="brand-link" className="inline-flex items-center">
        <img src="/img/affinity-logo.png" alt="Affinity Group" className="h-24 lg:h-28 w-auto object-contain" />
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="brand-link" className="brand-lockup inline-flex items-center gap-3">
      <span className="brand-mark h-11 md:h-12">
        <img src="/img/logo-mark.png" alt="" aria-hidden className="h-11 md:h-12 w-auto object-contain" />
      </span>
      <span className="grid leading-none">
        <strong className="brand-word whitespace-nowrap text-lg md:text-xl tracking-[.16em] font-semibold" style={{ color: light ? "var(--cream)" : "var(--ink)" }}>AFFINITY GROUP</strong>
        <small className="brand-tag hidden sm:block lg:hidden xl:block text-[9px] md:text-[10px] tracking-[.2em] mt-1.5 uppercase" style={{ color: "var(--gold-2)" }}>Representing Infinity Financial Advisory</small>
      </span>
    </Link>
  );
};

export const Header = () => {
  const { openQuiz } = useQuiz();
  const { pathname } = useLocation();
  const activeSection = useActiveSection(pathname);
  const isCareers = pathname.startsWith("/careers");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -30, x: "-50%", opacity: 0 }}
      animate={{ y: 0, x: "-50%", opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`site-header glass fixed z-[80] left-1/2 top-4 w-[min(1240px,calc(100%-24px))] rounded-full flex items-center justify-between pl-5 pr-2 py-2 transition-shadow duration-300 ${scrolled ? "is-condensed shadow-[0_18px_60px_rgba(0,0,0,.35)]" : ""}`}
    >
      <Brand />
      <nav className="hidden lg:flex items-center gap-4 xl:gap-7 text-[12px] xl:text-[13px] whitespace-nowrap text-[rgba(244,241,234,.72)]" aria-label="Primary">
        {NAV.map((n, idx) => {
          const sections = NAV_SECTIONS[n.path];
          const end = idx >= NAV.length - 2;
          if (!sections) {
            return (
              <NavLink key={n.path} to={n.path} onPointerEnter={() => prefetchRoute(n.path)} onTouchStart={() => prefetchRoute(n.path)} data-testid={`nav-${slug(n.label)}`} className={({ isActive }) => `relative py-2 transition-colors hover:text-[var(--gold-2)] ${isActive ? "text-[var(--gold-2)]" : ""}`}>
                {n.label}
              </NavLink>
            );
          }
          return (
            <div key={n.path} className="ag-nav__group relative">
              <NavLink to={n.path} onPointerEnter={() => prefetchRoute(n.path)} onTouchStart={() => prefetchRoute(n.path)} data-testid={`nav-${slug(n.label)}`} className={({ isActive }) => `relative inline-flex items-center py-2 transition-colors hover:text-[var(--gold-2)] ${isActive ? "text-[var(--gold-2)]" : ""}`}>
                {n.label} <ChevronDown className="ag-nav__caret" size={13} aria-hidden />
              </NavLink>
              <div className={`ag-nav-drop${end ? " ag-nav-drop--end" : ""}`} role="menu" data-testid={`nav-${slug(n.label)}-dropdown`}>
                <p className="ag-nav-drop__eyebrow">Jump to a section</p>
                {sections.map((s) => (
                  <Link key={s.hash} to={s.to || `${n.path}#${s.hash}`} role="menuitem" data-testid={`nav-section-${s.hash}`} className={`ag-nav-drop__item${n.path === pathname && s.hash === activeSection ? " is-active" : ""}`}>
                    <span className="ag-nav-drop__name">{s.name}</span>
                    {s.label ? <span className="ag-nav-drop__label">{s.label}</span> : null}
                  </Link>
                ))}
                <Link to={n.path} data-testid={`nav-${slug(n.label)}-all`} className="ag-nav-drop__all">View full {n.label} <ArrowRight size={13} /></Link>
              </div>
            </div>
          );
        })}
      </nav>
      <div className="flex items-center gap-2">
        {isCareers ? (
          <button data-testid="header-fit-check-button" onClick={() => openQuiz("header")} className="btn btn-gold btn-sm hidden md:inline-flex whitespace-nowrap">
            60-sec fit check <ArrowRight size={15} />
          </button>
        ) : (
          <Link to="/careers" data-testid="header-careers-link" className="btn btn-gold btn-sm hidden md:inline-flex whitespace-nowrap">
            <Briefcase size={14} /> Careers
          </Link>
        )}
        <button data-testid="mobile-menu-button" aria-label="Toggle navigation" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="lg:hidden grid place-items-center w-11 h-11 rounded-full text-[var(--cream)]">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
            className="glass absolute top-[calc(100%+10px)] left-0 right-0 rounded-2xl p-3 grid lg:hidden !bg-[rgba(7,20,22,.97)] max-h-[78vh] overflow-y-auto"
          >
            {NAV.map((n) => {
              const sections = NAV_SECTIONS[n.path];
              if (!sections) {
                return (
                  <NavLink key={n.path} to={n.path} onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`} className="px-4 py-4 border-b border-white/10 last:border-0 text-[15px] hover:text-[var(--gold-2)]">
                    {n.label}
                  </NavLink>
                );
              }
              return (
                <div key={n.path} className="border-b border-white/10">
                  <NavLink to={n.path} onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`} className="block px-4 pt-4 pb-2 text-[15px] hover:text-[var(--gold-2)]">{n.label}</NavLink>
                  <div className="pb-3 grid">
                    {sections.map((s) => (
                      <Link key={s.hash} to={s.to || `${n.path}#${s.hash}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-section-${s.hash}`} className={`flex items-baseline gap-2 pl-8 pr-4 py-2 transition-colors ${n.path === pathname && s.hash === activeSection ? "text-[var(--gold-2)]" : "text-[rgba(244,241,234,.72)] hover:text-[var(--gold-2)]"}`}>
                        <span className={`font-serif italic text-[16px] ${n.path === pathname && s.hash === activeSection ? "text-[var(--gold-2)]" : "text-[var(--gold-2)]"}`}>{s.name}</span>
                        {s.label ? <span className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</span> : null}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
            <NavLink to="/careers" onClick={() => setOpen(false)} data-testid="mobile-nav-careers" className="px-4 py-4 border-b border-white/10 text-[15px] text-[var(--gold-2)] font-semibold">Careers at Affinity</NavLink>
            <button data-testid="mobile-fit-check-button" onClick={() => { setOpen(false); openQuiz("mobile-menu"); }} className="btn btn-gold mt-3">60-sec fit check <ArrowRight size={15} /></button>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
